import { maxDustLogBufferBytes } from "./constants";
import type { DustLogEntry } from "./types";

/**
 * Tiny, dependency-free, length-capped ring buffer for connection-relevant DUST
 * logs. Each entry is timestamped at record time and surfaced via
 * {@link DustProvider.getConnectionDiagnostics} (which feeds Sentry
 * `extra.dust`) so we have a chronological log trail when debugging a flaky
 * connection in production.
 *
 * The cap counts the serialized line length (epoch-ms timestamp + space +
 * message) so the array stays under the configured budget. Eviction is
 * oldest-first: an individual message is never truncated, and the single newest
 * entry is always kept even if it alone exceeds the cap.
 */
export class DustLogBuffer {
  private readonly maxBytes: number;
  private entries: Array<DustLogEntry & { bytes: number }> = [];
  private totalBytes = 0;

  /**
   * @param maxBytes Length budget for the serialized log lines. Defaults to
   *   {@link maxDustLogBufferBytes}.
   */
  constructor(maxBytes: number = maxDustLogBufferBytes) {
    this.maxBytes = maxBytes;
  }

  /**
   * Serialize extra log args into a single appended string. Primitives via
   * String, Errors via their message, objects via JSON (falling back to String
   * if they aren't serializable).
   *
   * @param args Extra args passed alongside a log message.
   * @return The serialized suffix (leading space included), or "" when empty.
   */
  private formatArgs(args: unknown[]): string {
    if (args.length === 0) {
      return "";
    }
    const parts = args.map((arg) => {
      if (arg instanceof Error) {
        return arg.message;
      }
      if (arg === null || typeof arg !== "object") {
        return String(arg);
      }
      try {
        return JSON.stringify(arg);
      } catch (_) {
        return String(arg);
      }
    });
    return ` ${parts.join(" ")}`;
  }

  /**
   * Record a log message (with any extra args serialized and appended) at the
   * current timestamp, evicting oldest entries until the buffer fits the byte
   * budget (always keeping at least the newest).
   *
   * @param message The log message, with any prefix already applied.
   * @param args Extra args to serialize and append to the message.
   */
  record(message: string, args: unknown[] = []): void {
    const t = Date.now();
    const msg = `${message}${this.formatArgs(args)}`;
    const bytes = `${t} ${msg}`.length;
    this.entries.push({ t, msg, bytes });
    this.totalBytes += bytes;

    while (this.totalBytes > this.maxBytes && this.entries.length > 1) {
      const evicted = this.entries.shift()!;
      this.totalBytes -= evicted.bytes;
    }
  }

  /**
   * Format an epoch-ms timestamp as a local-time `HH:MM:SS.mmm` string.
   *
   * @param t The epoch-ms timestamp to format.
   * @return The formatted local time-of-day string.
   */
  private formatTime(t: number): string {
    // eslint-disable-next-line local-eslint-rules/no-new-date -- formatting an explicit captured epoch-ms timestamp, not reading "now"
    const d = new Date(t);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    const ms = String(d.getMilliseconds()).padStart(3, "0");
    return `${hh}:${mm}:${ss}.${ms}`;
  }

  /**
   * Serialize the buffer to an array of `<HH:MM:SS.mmm> <message>` lines
   * (local time), oldest first.
   *
   * @return The formatted log lines.
   */
  toArray(): string[] {
    return this.entries.map(({ t, msg }) => `${this.formatTime(t)} ${msg}`);
  }

  /**
   * Drop all buffered entries.
   */
  clear(): void {
    this.entries = [];
    this.totalBytes = 0;
  }
}
